import { PhotoIcon } from "@heroicons/react/20/solid"
import { clsx } from "clsx"
import { ChangeEventHandler, DragEventHandler, FC, useState } from "react"
import { useDb } from "domains/wordcloudplus/domains/database/database"
import { importProject } from "domains/wordcloudplus/domains/projects/actions/import"
import { Dialog, DialogBody, DialogTitle } from "@ui/dialog"

interface Props {
    visible: boolean
    onClose: () => void
    afterUpload: () => void
}

export const ImportModal: FC<Props> = ({ afterUpload, visible, onClose }) => {
    const { db } = useDb()
    const [dragInProgress, setDragInProgress] = useState(false)

    const upload = (files: FileList) => {
        if (files) {
            const file = files[0]
            const reader = new FileReader()
            reader.onload = () => {
                const text = reader.result
                if (typeof text === "string" && db != null) {
                    importProject(db, text).then(() => {
                        afterUpload()
                    })
                }
            }
            reader.readAsText(file)
        }
    }

    const onDragEnter: DragEventHandler<HTMLDivElement> = (ev) => {
        ev.preventDefault()
        setDragInProgress(true)
    }

    const onDragLeave: DragEventHandler<HTMLDivElement> = (ev) => {
        ev.preventDefault()
        setDragInProgress(false)
    }

    const onDrop: DragEventHandler<HTMLDivElement> = (ev) => {
        ev.preventDefault()
        setDragInProgress(false)
        const dt = ev.dataTransfer
        upload(dt.files)
    }

    const dragOverHandler: DragEventHandler<HTMLDivElement> = (ev) => {
        ev.preventDefault()
        setDragInProgress(true)
    }

    const handleInputChange: ChangeEventHandler<HTMLInputElement> = (ev) => {
        const files = ev.target.files
        if (files) {
            upload(files)
        }
    }

    return (
        <Dialog open={visible} onClose={onClose}>
            <DialogTitle>Import</DialogTitle>

            <DialogBody>
                <div
                    className={clsx(
                        "mt-2 flex justify-center rounded-lg border-2 border-dashed px-6 py-10",
                        dragInProgress ? "border-primary-600" : "border-gray-900/25"
                    )}
                    onDragEnter={onDragEnter}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                    onDragOver={dragOverHandler}
                >
                    <div className="text-center">
                        <PhotoIcon className="mx-auto size-12 text-gray-300" aria-hidden="true" />
                        <div className="mt-4 text-sm leading-6 text-gray-600">
                            <label
                                htmlFor="file-upload"
                                className="text-primary-600 focus-within:ring-primary-600 hover:text-primary-500 relative cursor-pointer rounded-md bg-white font-semibold focus-within:ring-2 focus-within:ring-offset-2 focus-within:outline-none"
                            >
                                <span>Upload a file</span>
                                <input
                                    id="file-upload"
                                    name="file-upload"
                                    type="file"
                                    className="sr-only"
                                    onChange={handleInputChange}
                                    accept=".json"
                                    multiple={false}
                                />
                            </label>
                            <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs leading-5 text-gray-600">JSON file exported from Research Wise AI</p>
                    </div>
                </div>
            </DialogBody>
        </Dialog>
    )
}
